"use client"

import { useState, useEffect } from "react"
import { useWallet } from "@solana/wallet-adapter-react"
import { Connection, PublicKey } from "@solana/web3.js"
import { IRMASubscriptionClient } from "@/lib/irma-client-browser"
import { apiClient } from "@/lib/api-client"
import { getAssociatedTokenAddress, getAccount } from "@solana/spl-token"
import { toast } from "@/hooks/use-toast"
import { CustomSelect } from "@/app/components/custom-select"

interface StakingFormProps {
  connection: Connection
  irmaMint: string
  onStakeComplete?: () => void
}

const STAKING_TIERS = [
  { value: 0, label: "Bronze", lockDays: 30, apy: 5 },
  { value: 1, label: "Silver", lockDays: 90, apy: 10 },
  { value: 2, label: "Gold", lockDays: 180, apy: 15 },
  { value: 3, label: "Platinum", lockDays: 365, apy: 25 },
]

export function StakingForm({ connection, irmaMint, onStakeComplete }: StakingFormProps) {
  const { publicKey, signTransaction, signAllTransactions } = useWallet()
  const [tier, setTier] = useState<number>(0)
  const [amount, setAmount] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [irmaBalance, setIrmaBalance] = useState<number>(0)
  const [config, setConfig] = useState<any>(null)
  const [estimatedRewards, setEstimatedRewards] = useState<number>(0)

  useEffect(() => {
    if (publicKey && connection && irmaMint && irmaMint !== "11111111111111111111111111111111") {
      loadIrmaBalance()
      loadStakingConfig()
    }
  }, [publicKey, connection, irmaMint])

  useEffect(() => {
    if (amount && tier !== undefined) calculateRewards()
  }, [amount, tier])

  const loadIrmaBalance = async () => {
    if (!publicKey || !connection || !irmaMint) return
    try {
      const mint = new PublicKey(irmaMint)
      const ata = await getAssociatedTokenAddress(mint, publicKey)
      const account = await getAccount(connection, ata)
      setIrmaBalance(Number(account.amount) / 1e9)
    } catch (err) {
      setIrmaBalance(0)
    }
  }

  const loadStakingConfig = async () => {
    try {
      const configData = await apiClient.getStakingConfig()
      setConfig(configData)
    } catch (err: any) {
      console.error("Failed to load staking config:", err)
      setConfig({ bronzeApy: "500", silverApy: "1000", goldApy: "1500", platinumApy: "2500", bronzePenalty: "5000", silverPenalty: "4000", goldPenalty: "3000", platinumPenalty: "2000" })
    }
  }

  const calculateRewards = () => {
    if (!amount || !config) return
    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum <= 0) { setEstimatedRewards(0); return }
    const selectedTier = STAKING_TIERS[tier]
    const apy = selectedTier.apy / 100
    const rewards = (amountNum * apy * selectedTier.lockDays) / 365
    setEstimatedRewards(rewards)
  }

  const handleStake = async () => {
    const nonce = Date.now()
    if (!publicKey || !signTransaction) { setError("Please connect your wallet"); return }
    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum <= 0) { setError("Please enter a valid amount"); return }
    if (amountNum > irmaBalance) { setError("Insufficient IRMA balance"); return }

    setLoading(true); setError("")

    try {
      const wallet = {
        publicKey,
        signTransaction: async (tx: any) => {
          if (!signTransaction) throw new Error("Wallet signTransaction not available")
          try {
            const signed = await signTransaction(tx)
            return signed
          } catch (err: any) {
            if (err.message?.includes("User rejected") || err.message?.includes("User cancelled")) throw new Error("Transaction cancelled by user")
            throw err
          }
        },
        signAllTransactions: async (txs: any[]) => {
          if (!signAllTransactions) throw new Error("Wallet signAllTransactions not available")
          try {
            return await signAllTransactions(txs)
          } catch (err: any) {
            if (err.message?.includes("User rejected") || err.message?.includes("User cancelled")) throw new Error("Transaction cancelled by user")
            throw err
          }
        },
      }

      const client = new IRMASubscriptionClient(connection, wallet)
      const mintPubkey = new PublicKey(irmaMint)

      try {
        const existingStake = await Promise.race([
          client.getStakingAccount(publicKey, tier),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Check timeout")), 5000))
        ]) as any
        if (existingStake && existingStake.isActive) {
          setError(`You already have an active stake in ${STAKING_TIERS[tier].label} tier. Please unstake first or choose a different tier.`)
          setLoading(false); return
        }
      } catch (err) {
        console.log("No existing stake found for this tier (or check timed out), proceeding with new stake")
      }

      try {
        const vaultInit = await apiClient.initializeStakingVault()
        if (!vaultInit.success && !vaultInit.error?.includes("already initialized")) console.warn("Failed to initialize vault:", vaultInit.error)
        const configInit = await apiClient.initializeStakingConfig()
        if (!configInit.success && !configInit.error?.includes("already initialized")) console.warn("Failed to initialize config:", configInit.error)
      } catch (initError: any) {
        console.warn("Error during initialization check:", initError)
      }

      const tx = await Promise.race([
        client.stake(publicKey, amountNum, tier, mintPubkey, nonce),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Stake transaction timeout. Please try again.")), 60000))
      ]) as string

      await Promise.race([
        connection.confirmTransaction(tx, "confirmed"),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Transaction confirmation timeout")), 30000))
      ])

      toast({ title: "Staking Successful", description: `Staked ${amountNum} IRMA in ${STAKING_TIERS[tier].label} tier. Transaction: ${tx.slice(0, 8)}...` })
      setAmount(""); setEstimatedRewards(0)
      await loadIrmaBalance()
      onStakeComplete?.()
    } catch (err: any) {
      const errorMessage = err.message || err.toString() || "Staking failed"
      let userFriendlyMessage = errorMessage
      if (errorMessage.includes("AccountAlreadyInitialized") || errorMessage.includes("already in use")) {
        userFriendlyMessage = `You already have a stake in ${STAKING_TIERS[tier].label} tier. Please unstake first or choose a different tier.`
      } else if (errorMessage.includes("InvalidStaker") || errorMessage.includes("Unauthorized")) {
        userFriendlyMessage = "Invalid staker address. Please reconnect your wallet."
      } else if (errorMessage.includes("Insufficient")) {
        userFriendlyMessage = "Insufficient balance for staking"
      } else if (errorMessage.includes("Account does not exist") || errorMessage.includes("not initialized")) {
        userFriendlyMessage = "Staking vault or config not initialized. Please contact admin."
      } else if (errorMessage.includes("0x1")) {
        userFriendlyMessage = "Transaction failed. Please check your balance and try again."
      }
      setError(userFriendlyMessage)
    } finally {
      setLoading(false)
    }
  }

  const selectedTier = STAKING_TIERS[tier]

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
        <div className="px-1 mb-3">
          <div className="text-lg font-bold text-white mb-1">Stake IRMA Tokens</div>
          <div className="text-sm text-[#8B949E] font-medium">Lock your tokens and earn rewards</div>
        </div>

        <div className="space-y-3">
          {/* Wallet Info */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
            <label className="block text-sm text-[#8B949E] font-medium mb-2 ml-1">Connected Wallet</label>
            <div className="flex justify-between items-center">
              <span className="text-[#8B949E] text-sm">
                {publicKey ? publicKey.toBase58().slice(0, 8) + "..." : "Not connected"}
              </span>
              {publicKey && <span className="font-bold text-lg text-white">{irmaBalance.toFixed(4)} IRMA</span>}
            </div>
          </div>

          {/* Staking Tier */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
            <label className="block text-sm text-[#8B949E] font-medium mb-3 ml-1">Staking Tier</label>
            <CustomSelect
              value={tier.toString()}
              onValueChange={(v) => setTier(parseInt(v))}
              options={STAKING_TIERS.map((t) => ({
                value: t.value.toString(),
                label: `${t.label} - ${t.lockDays} days, ${t.apy}% APY`,
                icon: (
                  <div className="w-7 h-7 rounded-full bg-[#25e4e8] flex items-center justify-center text-black font-bold text-sm">
                    {t.label.charAt(0)}
                  </div>
                ),
              }))}
              triggerClassName="flex items-center gap-2.5 bg-[#13161C] hover:bg-[#1A1D24] py-2 pl-2 pr-3.5 rounded-full border border-white/5 transition w-full font-bold text-lg text-white tracking-wide"
              contentClassName="bg-[#13161C] border border-white/10 rounded-lg shadow-xl overflow-hidden"
              itemClassName="px-3 py-2 text-white hover:bg-[#1C1F26] transition cursor-pointer text-sm"
            />
            <div className="text-xs text-[#8B949E] mt-2 ml-1">
              Lock period: {selectedTier.lockDays} days | APY: {selectedTier.apy}%
            </div>
          </div>

          {/* Amount Input */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 pb-5 border border-transparent focus-within:border-white/10 transition">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm text-[#8B949E] font-medium ml-1">Amount (IRMA)</label>
              <button onClick={() => setAmount(irmaBalance.toFixed(4))} className="text-xs text-[#8B949E] hover:text-white transition-colors font-medium">Max</button>
            </div>
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#25e4e8] flex items-center justify-center text-black font-bold text-sm">I</div>
                <span className="font-bold text-lg text-white tracking-wide">IRMA</span>
              </div>
              <div className="flex-grow flex flex-col items-end">
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="0"
                  step="0.0001"
                  className="w-full bg-transparent text-right text-[32px] font-semibold outline-none text-white placeholder-[#3b4153] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          </div>

          {/* Estimated Rewards */}
          {estimatedRewards > 0 && (
            <div className="bg-[#1C1F26] rounded-2xl p-4 border border-[#25e4e8]/20">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-[#8B949E] font-medium">Estimated Rewards:</span>
                <span className="font-bold text-lg text-[#25e4e8]">{estimatedRewards.toFixed(4)} IRMA</span>
              </div>
              <p className="text-xs text-[#8B949E]">Based on {selectedTier.lockDays} day lock period</p>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400 text-center">{error}</div>
          )}

          <div className="mt-3">
            {!publicKey ? (
              <div className="text-center">
                <p className="text-sm text-[#8B949E] mb-3">Connect your wallet to stake</p>
                <button className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98]">Connect Wallet</button>
              </div>
            ) : (
              <button
                onClick={handleStake}
                disabled={loading || !amount || parseFloat(amount) <= 0}
                className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] disabled:bg-[#8B949E] disabled:cursor-not-allowed text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98] disabled:active:scale-100 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent" /> Processing...</>
                ) : "Stake IRMA"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
